import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIERS = [
  {
    id: "starter",
    label: "Starter",
    amount: 25,
    icon: "fa-seedling",
    benefit: "Funds journals, mirror-test worksheets, and Crash Proof workbooks for one youth.",
    tag: "$25"
  },
  {
    id: "advocate",
    label: "Advocate",
    amount: 100,
    icon: "fa-shield-halved",
    benefit: "Sponsors a youth's full 8-week Crash Proof Curriculum cycle — group + 1-on-1 mentorship.",
    tag: "$100",
    featured: true
  },
  {
    id: "champion",
    label: "Champion",
    amount: 500,
    icon: "fa-crown",
    benefit: "Underwrites a full mentorship cohort — curriculum, journals, brotherhood circle, and signed copies of the book for an entire group.",
    tag: "$500"
  }
];

export default function SponsorAYouth() {
  const [selected, setSelected] = useState("advocate");
  const [custom, setCustom] = useState("");
  const [donorEmail, setDonorEmail] = useState("");
  const [donorName, setDonorName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSponsor = async (e) => {
    e?.preventDefault?.();
    if (submitting) return;

    const customAmt = parseFloat(custom);
    const useCustom = !isNaN(customAmt) && customAmt > 0;

    if (!useCustom && !selected) {
      toast.error("Pick a tier or enter a custom amount.");
      return;
    }

    setSubmitting(true);
    try {
      const body = {
        frequency: "one_time",
        origin_url: window.location.origin,
        donor_email: donorEmail || null,
        donor_name: donorName || null,
      };
      if (useCustom) body.custom_amount = customAmt;
      else body.package_id = selected;

      const { data } = await axios.post(`${API}/donations/checkout`, body);
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.detail || "Couldn't start checkout. Try again or email info@mrgreenbay.com.";
      toast.error(msg);
      setSubmitting(false);
    }
  };

  return (
    <section
      id="sponsor"
      data-testid="sponsor-section"
      className="relative overflow-hidden bg-ink-800 py-28 md:py-36"
    >
      <div className="pointer-events-none absolute inset-0 bg-grain opacity-30 mix-blend-overlay" />
      <div className="pointer-events-none absolute -left-32 top-1/3 h-72 w-72 rounded-full bg-gold/10 blur-[140px]" />
      <div className="pointer-events-none absolute -right-32 bottom-1/4 h-72 w-72 rounded-full bg-gold/5 blur-[160px]" />

      <div className="relative mx-auto max-w-7xl px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="mx-auto mb-14 max-w-3xl text-center"
        >
          <div className="mb-4 flex items-center justify-center gap-3 text-xs uppercase tracking-[0.3em] text-gold">
            <span className="h-px w-10 bg-gold/60" />
            Sponsor A Youth
            <span className="h-px w-10 bg-gold/60" />
          </div>
          <h2 className="font-display text-4xl font-bold leading-tight text-white md:text-5xl lg:text-6xl">
            Friends don't let friends <span className="text-stroke-gold">crash out</span>.
            <span className="block text-gradient-gold">Help one cash out instead.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-zinc-300">
            Every dollar funds curriculum, mentorship, journals, and brotherhood circles — direct
            investment in a young person's blueprint.
          </p>
        </motion.div>

        {/* Tier cards */}
        <div className="grid gap-6 md:grid-cols-3">
          {TIERS.map((t, i) => {
            const active = selected === t.id && !custom;
            return (
              <motion.button
                key={t.id}
                type="button"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.05 * i, duration: 0.5 }}
                onClick={() => { setSelected(t.id); setCustom(""); }}
                data-testid={`sponsor-tier-${t.id}`}
                className={`group relative overflow-hidden rounded-3xl border p-7 text-left transition-all ${
                  active
                    ? "border-gold bg-gradient-to-br from-ink-700 to-ink-900 shadow-[0_30px_80px_-20px_rgba(197,160,89,0.4)]"
                    : "border-white/5 bg-ink-700/60 hover:-translate-y-0.5 hover:border-gold/40"
                }`}
              >
                {t.featured && !active && (
                  <div className="absolute right-4 top-4 rounded-full border border-gold/40 bg-black/60 px-3 py-1 text-[10px] uppercase tracking-[0.25em] text-gold">
                    Most Impact
                  </div>
                )}
                {active && (
                  <div className="absolute right-4 top-4 flex h-7 w-7 items-center justify-center rounded-full bg-gold text-black">
                    <i className="fa-solid fa-check text-xs" />
                  </div>
                )}
                <div className="flex items-center gap-4">
                  <span className={`flex h-12 w-12 items-center justify-center rounded-full border ${active ? "border-gold bg-gold/20 text-gold" : "border-gold/30 bg-black/40 text-gold"}`}>
                    <i className={`fa-solid ${t.icon} text-lg`} />
                  </span>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">{t.label}</div>
                    <div className="font-display text-3xl text-white">{t.tag}</div>
                  </div>
                </div>
                <p className="mt-5 text-sm leading-relaxed text-zinc-300">{t.benefit}</p>
              </motion.button>
            );
          })}
        </div>

        {/* Custom amount + donor info + CTA */}
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2, duration: 0.6 }}
          onSubmit={onSponsor}
          data-testid="sponsor-form"
          className="mt-8 grid gap-4 rounded-3xl border border-white/5 bg-black/40 p-6 md:grid-cols-12 md:gap-5 md:p-8"
        >
          <div className="md:col-span-3">
            <label className="mb-1.5 block text-[10px] uppercase tracking-[0.25em] text-zinc-500">
              Or Custom Amount (USD)
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gold">$</span>
              <input
                type="number"
                min="5"
                step="1"
                value={custom}
                onChange={(e) => { setCustom(e.target.value); }}
                placeholder="250"
                data-testid="sponsor-custom-amount"
                className="w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 pl-8 text-sm text-white placeholder-zinc-600 outline-none focus:border-gold/60 focus:ring-2 focus:ring-gold/20"
              />
            </div>
          </div>
          <div className="md:col-span-4">
            <label className="mb-1.5 block text-[10px] uppercase tracking-[0.25em] text-zinc-500">
              Your Name (optional)
            </label>
            <input
              type="text"
              value={donorName}
              onChange={(e) => setDonorName(e.target.value)}
              data-testid="sponsor-donor-name"
              className="w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm text-white placeholder-zinc-600 outline-none focus:border-gold/60 focus:ring-2 focus:ring-gold/20"
            />
          </div>
          <div className="md:col-span-5">
            <label className="mb-1.5 block text-[10px] uppercase tracking-[0.25em] text-zinc-500">
              Email (for receipt)
            </label>
            <input
              type="email"
              value={donorEmail}
              onChange={(e) => setDonorEmail(e.target.value)}
              placeholder="you@email.com"
              data-testid="sponsor-donor-email"
              className="w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm text-white placeholder-zinc-600 outline-none focus:border-gold/60 focus:ring-2 focus:ring-gold/20"
            />
          </div>
          <div className="md:col-span-12 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-zinc-500">
              <i className="fa-solid fa-lock mr-1.5 text-gold" />
              Secure one-time checkout via Stripe.
            </p>
            <button
              type="submit"
              disabled={submitting}
              data-testid="sponsor-submit"
              className="btn-gold rounded-full px-8 py-4 text-sm font-semibold uppercase tracking-wider disabled:opacity-60"
            >
              {submitting ? (
                <><i className="fa-solid fa-circle-notch fa-spin mr-2" /> Redirecting…</>
              ) : (
                <><i className="fa-solid fa-fire mr-2" /> Sponsor Now</>
              )}
            </button>
          </div>
        </motion.form>

        <p className="mt-8 text-center text-xs uppercase tracking-[0.25em] text-zinc-500">
          100% of proceeds support YES — Youth Empowerment Summit programming
        </p>
      </div>
    </section>
  );
}
